import { useCallback, useContext, useEffect, useState } from "react";

import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";

import Btn from "../../../../../components/buttons/Btn";
import TextFieldErp from "../../../../../components/basics/TextFieldErp";
import SelectErp from "../../../../../components/basics/SelectErp";
import MaskService from "../../../../../utils/mask/maskService";
import { validNotNull } from "../../../../../utils/validations";
import { CompanyRuralEstablishmentsContext } from "../contexts/CompanyRuralEstablishmentsContext";
import useCompanyRuralEstablishmentsController from "../controllers/CompanyRuralEstablishmentsController";
import { QueryParamsContext } from "../../../../../context/QueryParamsContext";

const countryInitialValue = {
  codigo: "1058",
  descricao: "BRASIL",
  siscomex: "105",
};

const CompanyModalRuralEstablishment = ({
  open,
  setOpen,
  rowEdit,
  setRowEdit,
}) => {
  const { queryParams } = useContext(QueryParamsContext);

  const { listUf, listCounty, listNation } = useContext(
    CompanyRuralEstablishmentsContext
  );

  const {
    handleCep,
    handleSaveCompanyRuralEstablishment,
    handleGetRuralEstablishments,
  } = useCompanyRuralEstablishmentsController();

  const [description, setDescription] = useState("");
  const [stateRegistration, setStateRegistration] = useState("");
  const [cep, setCep] = useState("");
  const [address, setAddress] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [complement, setComplement] = useState("");
  const [district, setDistrict] = useState("");
  const [uf, setUf] = useState(null);
  const [country, setCountry] = useState(countryInitialValue);
  const [contactName, setContactName] = useState("");
  const [telephone, setTelephone] = useState("");
  const [active, setActive] = useState(true);
  const [county, setCounty] = useState("");
  const [main, setMain] = useState(false);

  const [errorsModal, setErrorsModal] = useState({});

  useEffect(() => {
    if (!rowEdit) return;

    setDescription(rowEdit.description || "");
    setStateRegistration(rowEdit.stateInscription || "");
    setCep(MaskService.toMask("cep", rowEdit.address?.zipCode || ""));
    setAddress(rowEdit.address?.publicPlace || "");
    setAddressNumber(rowEdit.address?.number || "");
    setComplement(rowEdit.address?.additionalData || "");
    setDistrict(rowEdit.address?.district || "");
    setCounty(
      rowEdit.address?.city?.code
        ? listCounty.find((opt) => opt.codigo === rowEdit.address?.city?.code)
        : null
    );
    setUf(
      rowEdit.address?.state?.ibgeCode
        ? listUf.find((opt) => opt.codibge === rowEdit.address?.state?.ibgeCode)
        : null
    );
    setCountry(
      rowEdit.address?.country?.code
        ? listNation.find(
            (opt) => opt.codigo === rowEdit.address?.country?.code
          )
        : null
    );
    setContactName(rowEdit.contacts[0]?.name || "");
    setTelephone(
      MaskService.toMask("cel-phone", rowEdit.contacts[0]?.phone || "")
    );
    setMain(rowEdit.mainEstablishment || false);
    setActive(rowEdit.active || false);
  }, [listCounty, listUf, listNation, rowEdit]);

  const handleChangeUf = (e) => {
    setUf(e);
    setCounty(null);
  };

  const handleCloseModal = useCallback(() => {
    setRowEdit(null);
    setOpen(false);
  }, [setOpen, setRowEdit]);

  const handleValidations = useCallback(async () => {
    const resultValidation = validNotNull([
      { key: "address", value: address },
      { key: "stateRegistration", value: stateRegistration },
      { key: "uf", value: uf },
      { key: "county", value: county },
      { key: "district", value: district },
      { key: "country", value: country },
      { key: "contactName", value: contactName },
      { key: "telephone", value: telephone },
      { key: "cep", value: cep },
    ]);

    if (!description) {
      resultValidation.error = true;
      resultValidation.errorsValidation.description = "Campo obrigatório";
    }

    if (cep && !MaskService.isValid("cep", cep)) {
      resultValidation.error = true;
      resultValidation.errorsValidation.cep = "CEP inválido";
    }

    if (telephone && !MaskService.isValid("cel-phone", telephone)) {
      resultValidation.error = true;
      resultValidation.errorsValidation.telephone = "Telefone inválido";
    }

    setErrorsModal(resultValidation.errorsValidation);

    if (resultValidation.error) return;

    const establishmentId = rowEdit?.id || null;

    const body = {
      description: description,
      stateInscription: stateRegistration,
      active: active,
      mainEstablishment: main,
      companyId: queryParams.empresa_id,
      address: {
        publicPlace: address,
        zipCode: cep.replace(/[^0-9]+/g, ""),
        number: addressNumber,
        additionalData: complement,
        district: district,
        cityId: county ? county.codigo : null,
        stateId: uf ? uf.id : null,
        countryId: country ? country.codigo : null,
      },
      contact: {
        name: contactName,
        phone: telephone.replace(/[^0-9]+/g, ""),
      },
    };

    const saveCallback = await handleSaveCompanyRuralEstablishment({
      body,
      establishmentId,
    });

    if (saveCallback) {
      handleCloseModal();
      handleGetRuralEstablishments({});
    }
  }, [
    active,
    address,
    addressNumber,
    cep,
    complement,
    contactName,
    country,
    county,
    description,
    district,
    handleCloseModal,
    handleGetRuralEstablishments,
    handleSaveCompanyRuralEstablishment,
    main,
    queryParams?.empresa_id,
    rowEdit?.id,
    stateRegistration,
    telephone,
    uf,
  ]);

  return (
    <>
      <Dialog
        open={open}
        maxWidth="lg"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {`${rowEdit ? "Editar" : "Cadastrar"} Estabelecimento Rural`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <TextFieldErp
                error={errorsModal.description || false}
                helperText={errorsModal.description || ""}
                label="Descrição*"
                maxLength={50}
                value={description}
                setValue={setDescription}
              />
            </Grid>

            <Grid item lg={3} md={6} sm={6} xs={12}>
              <TextFieldErp
                error={errorsModal.stateRegistration || false}
                helperText={errorsModal.stateRegistration || ""}
                label="Inscrição Estadual*"
                value={stateRegistration}
                onChange={({ target: { value } }) =>
                  setStateRegistration(
                    (value || "").replace(/[^0-9./-]+/g, "").substring(0, 20)
                  )
                }
              />
            </Grid>

            <Grid item lg={3} md={3} sm={6} xs={12}>
              <TextFieldErp
                error={errorsModal.cep || false}
                helperText={errorsModal.cep || ""}
                label="CEP*"
                maxLength={12}
                value={MaskService.toMask("cep", cep)}
                onChange={(e) => {
                  handleCep({
                    prop: e,
                    setCep,
                    setUf,
                    setAddress,
                    setDistrict,
                    setComplement,
                    setCounty,
                  });
                }}
                mask="cep"
                endAdornment={<SearchIcon onClick={() => {}} />}
              />
            </Grid>

            <Grid item lg={6} md={7} sm={6} xs={12}>
              <TextFieldErp
                error={errorsModal.address || false}
                helperText={errorsModal.address || ""}
                label="Endereço*"
                maxLength={50}
                value={address}
                setValue={setAddress}
              />
            </Grid>

            <Grid item lg={1} md={2} sm={6} xs={12}>
              <TextFieldErp
                label="Número"
                value={addressNumber}
                onChange={({ target: { value } }) =>
                  setAddressNumber(
                    (value || "").replace(/[^0-9]+/g, "").substring(0, 10)
                  )
                }
              />
            </Grid>

            <Grid item lg={1} md={2} sm={6} xs={12}>
              <SelectErp
                error={errorsModal.uf || false}
                helperText={errorsModal.uf || ""}
                label="Estado*"
                value={uf}
                setValue={setUf}
                onChange={(e) => handleChangeUf(e)}
                optionsList={listUf}
                descriptionField="estado"
              />
            </Grid>

            <Grid item lg={4} md={4} sm={6} xs={12}>
              <SelectErp
                error={errorsModal.county || false}
                helperText={errorsModal.county || ""}
                label="Municipio*"
                value={county}
                setValue={setCounty}
                optionsList={
                  uf
                    ? listCounty.filter(
                        (opt) => opt.estado_codigo === uf.codibge
                      )
                    : []
                }
                descriptionField="municipio"
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextFieldErp
                label="Complemento"
                maxLength={50}
                value={complement}
                setValue={setComplement}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextFieldErp
                error={errorsModal.district || false}
                helperText={errorsModal.district || ""}
                label="Bairro*"
                maxLength={50}
                value={district}
                setValue={setDistrict}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <SelectErp
                error={errorsModal.country || false}
                helperText={errorsModal.country || ""}
                label="País*"
                value={country}
                setValue={setCountry}
                optionsList={listNation}
                descriptionField="descricao"
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextFieldErp
                error={errorsModal.contactName || false}
                helperText={errorsModal.contactName || ""}
                label="Nome do Contato*"
                maxLength={50}
                value={contactName}
                setValue={setContactName}
              />
            </Grid>

            <Grid item lg={3} md={6} sm={6} xs={12}>
              <TextFieldErp
                error={errorsModal.telephone || false}
                helperText={errorsModal.telephone || ""}
                label="Telefone do Contato*"
                maxLength={50}
                value={telephone}
                setValue={setTelephone}
                mask="cel-phone"
              />
            </Grid>

            <Grid item container lg={4} md={6} sm={6} xs={12}>
              <Grid item xs={6}>
                <FormControlLabel
                  value="end"
                  control={
                    <Switch
                      checked={active}
                      onChange={({ target: { checked } }) => {
                        if (!checked) setMain(false);

                        setActive((current) => !current);
                      }}
                      color="primary"
                    />
                  }
                  label="Ativo"
                  labelPlacement="end"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  value="end"
                  control={
                    <Switch
                      checked={main}
                      onChange={() => setMain((current) => !current)}
                      color="primary"
                      disabled={!active}
                    />
                  }
                  label="Principal"
                  labelPlacement="end"
                />
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Btn label="Cancelar" bgcolor="tertiary" onClick={handleCloseModal} />
          <Btn label="Salvar" onClick={handleValidations} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyModalRuralEstablishment;
